// ─────────────────────────────────────────────────────────────
// Admin Screen — Gestão de usuários
// ─────────────────────────────────────────────────────────────
import { ref, set, get }                  from 'firebase/database';
import { db }                             from '@/firebase';
import { getState, getUsersCache,
         setUsersCache }                  from '@/store/tripState';
import { openModal }                      from '@/ui/modal';
import { toastSuccess, toastError }       from '@/ui/toast';
import { logAction, logPageView }         from '@/services/logger.service';
import { initials, avatarColor, g,
         sanitize }                        from '@/helpers';
import { changeOtherUserPassword }        from '@/services/auth.service';
import { openChangePasswordModal }        from '@/ui/passwordModal';
import type { TravelleUser }              from '@/types';
import { USERS_PER_PAGE }                  from '@/constants';

let usersPageCurrent = 1;
let usersFiltered: [string, TravelleUser][] = [];

// ── Load all users ────────────────────────────────────────────
export async function loadAdminUsers(): Promise<void> {
  try {
  const snap = await get(ref(db, 'users'));
  const all  = (snap.val() ?? {}) as Record<string, TravelleUser>;
  setUsersCache(all);

  usersFiltered = Object.entries(all).sort(([, a], [, b]) =>
    (a.displayName || a.email).localeCompare(b.displayName || b.email, 'pt-BR')
  );
  usersPageCurrent = 1;
  renderUsersPage();
  } catch (e) { toastError('Erro ao carregar usuários: ' + (e instanceof Error ? e.message : String(e))); }
}

export function filterUsers(): void {
  const q   = (document.getElementById('users-search') as HTMLInputElement)?.value.toLowerCase().trim() ?? '';
  const all = Object.entries(getUsersCache()).sort(([, a], [, b]) =>
    (a.displayName || a.email).localeCompare(b.displayName || b.email, 'pt-BR')
  );
  usersFiltered = q
    ? all.filter(([, u]) =>
        (u.displayName ?? '').toLowerCase().includes(q) ||
        (u.email ?? '').toLowerCase().includes(q))
    : all;
  usersPageCurrent = 1;
  renderUsersPage();
}

export function renderUsersPage(): void {
  const el    = document.getElementById('users-list');
  const pgEl  = document.getElementById('users-pagination');
  const cntEl = document.getElementById('users-count');
  const total = usersFiltered.length;
  if (!el) return;

  if (!total) {
    el.innerHTML = '<div class="empty" style="padding:1rem">Nenhum usuário encontrado.</div>';
    if (pgEl)  pgEl.innerHTML  = '';
    if (cntEl) cntEl.textContent = '';
    return;
  }

  const totalPages = Math.ceil(total / USERS_PER_PAGE);
  if (usersPageCurrent > totalPages) usersPageCurrent = totalPages;
  const start = (usersPageCurrent - 1) * USERS_PER_PAGE;
  const slice = usersFiltered.slice(start, start + USERS_PER_PAGE);
  if (cntEl) cntEl.textContent = `${total} usuário${total !== 1 ? 's' : ''}`;

  el.innerHTML = slice.map(([uid, u], i) => `
    <div class="user-row">
      <div class="user-avatar" style="background:${avatarColor(start+i)};color:#fff">${initials(u.displayName||u.email)}</div>
      <div class="user-info">
        <div class="user-name">${sanitize(u.displayName ?? '—')} ${u.role==='admin'?'<span class="badge b-info" style="font-size:10px">Admin</span>':''}</div>
        <div class="user-email">${sanitize(u.email ?? '')}</div>
      </div>
      <div style="display:flex;gap:4px">
        <button class="btn-sm" onclick="window.__adminChangePassword('${uid}','${u.email}')">🔑 Senha</button>
        ${u.role !== 'admin' ? `<button class="btn-sm" onclick="window.__setAdmin('${uid}')">↑ Admin</button>` : ''}
      </div>
    </div>`).join('');

  // Pagination
  if (!pgEl || totalPages <= 1) { if (pgEl) pgEl.innerHTML = ''; return; }

  const btn = (p: number, label: string, active = false, disabled = false) =>
    `<button onclick="window.__goUsersPage(${p})" ${disabled?'disabled':''} style="padding:5px 10px;border-radius:var(--r);border:0.5px solid var(--border2);font-size:12px;cursor:pointer;background:${active?'var(--text)':'transparent'};color:${active?'var(--bg)':'var(--muted)'}">${label}</button>`;

  let pages = btn(1, '1', usersPageCurrent===1);
  if (usersPageCurrent > 3) pages += '<span style="color:var(--muted);font-size:13px">…</span>';
  for (let p = Math.max(2, usersPageCurrent-1); p <= Math.min(totalPages-1, usersPageCurrent+1); p++)
    pages += btn(p, String(p), usersPageCurrent===p);
  if (usersPageCurrent < totalPages - 2) pages += '<span style="color:var(--muted);font-size:13px">…</span>';
  if (totalPages > 1) pages += btn(totalPages, String(totalPages), usersPageCurrent===totalPages);

  pgEl.innerHTML =
    btn(usersPageCurrent-1, '‹', false, usersPageCurrent===1) +
    pages +
    btn(usersPageCurrent+1, '›', false, usersPageCurrent===totalPages);
}

export function goUsersPage(p: number): void {
  const totalPages = Math.ceil(usersFiltered.length / USERS_PER_PAGE);
  if (p < 1 || p > totalPages) return;
  usersPageCurrent = p;
  renderUsersPage();
}

// ── Set admin role ────────────────────────────────────────────
export async function setAdmin(userId: string): Promise<void> {
  await set(ref(db, `users/${userId}/role`), 'admin');
  logAction('user_promoted_admin', `User promoted to admin: ${userId}`, { userId });
  toastSuccess('Usuário promovido a administrador.');
  loadAdminUsers();
}

// ── Change password (admin) ───────────────────────────────────
export function adminChangePassword(targetUid: string, email: string): void {
  openChangePasswordModal(email, async (newPassword) => {
    await changeOtherUserPassword(targetUid, newPassword);
  });
}

// ── Create user (secondary app workaround) ────────────────────
export function openUserModal(): void {
  openModal('Criar novo usuário', `
    <div class="fg"><label>Nome</label><input id="f-user-name" placeholder="Nome completo"></div>
    <div class="fg"><label>E-mail</label><input type="email" id="f-user-email" placeholder="email@exemplo.com"></div>
    <div class="fg"><label>Senha inicial</label><input type="password" id="f-user-pass" placeholder="Mínimo 8 caracteres"></div>`,
    async () => {
      const name  = g('f-user-name');
      const email = g('f-user-email');
      const pass  = g('f-user-pass');
      if (!name || !email || !pass) throw new Error('Preencha todos os campos.');
      if (pass.length < 8) throw new Error('Senha precisa ter ao menos 8 caracteres.');

      // Create via secondary app to avoid logging out current admin
      const { initializeApp, deleteApp } = await import('firebase/app');
      const { getAuth, createUserWithEmailAndPassword, updateProfile } = await import('firebase/auth');
      const { currentUser } = getState();
      if (!currentUser) return;

      const secondaryApp = initializeApp(
        (await import('@/firebase')).firebaseConfig, 'secondary'
      );
      try {
        const secondaryAuth = getAuth(secondaryApp);
        const cred = await createUserWithEmailAndPassword(secondaryAuth, email, pass);
        await updateProfile(cred.user, { displayName: name });
        await set(ref(db, `users/${cred.user.uid}`), {
          uid: cred.user.uid, displayName: name, email, role: 'user',
        });
        logAction('user_created', `New user created: ${email}`, { email });
        toastSuccess(`Usuário "${name}" criado!`);
        loadAdminUsers();
      } finally {
        await deleteApp(secondaryApp);
      }
    });
}

// ── Register globals ──────────────────────────────────────────
export function registerGlobals(): void {
  const w = window as unknown as Record<string, unknown>;
  w['__adminChangePassword'] = adminChangePassword;
  w['__setAdmin']            = setAdmin;
  w['__goUsersPage']         = goUsersPage;
  w['__openUserModal']       = openUserModal;
}

// ── Admin tab navigation ──────────────────────────────────────
export function switchAdminTab(tab: 'dashboard' | 'users' | 'logs'): void {
  ['dashboard', 'users', 'logs'].forEach(t => {
    const section = document.getElementById(`admin-section-${t}`);
    const btn     = document.getElementById(`admin-tab-${t}`);
    if (section) section.style.display = t === tab ? 'block' : 'none';
    if (btn)     btn.classList.toggle('active', t === tab);
  });

  if (tab === 'dashboard') {
    import('@/screens/dashboard.screen').then(({ renderDashboard }) => renderDashboard());
  }
  if (tab === 'logs') {
    renderLogsTab();
  }
  logPageView(`admin:${tab}`, { screen: 'admin', adminTab: tab });
}

// ── Refresh dashboard ─────────────────────────────────────────
export function refreshDashboard(): void {
  import('@/screens/dashboard.screen').then(({ renderDashboard }) => renderDashboard());
}

// ── Logs tab state ───────────────────────────────────────────
interface LogRecord {
  level: string; action: string; message: string;
  email: string | null; timestamp: number;
}
let _allErrors:  LogRecord[] = [];
let _allActions: LogRecord[] = [];
let _allAccess:  LogRecord[] = [];

export async function renderLogsTab(): Promise<void> {
  const errEl = document.getElementById('logs-errors-list');
  const actEl = document.getElementById('logs-actions-list');
  const accessEl = document.getElementById('logs-access-list');
  if (errEl) errEl.innerHTML = '<div class="empty">Carregando...</div>';
  if (actEl) actEl.innerHTML = '<div class="empty">Carregando...</div>';
  if (accessEl) accessEl.innerHTML = '<div class="empty">Carregando...</div>';

  try {
    const { get, query, ref: dbRef,
            orderByChild, limitToLast } = await import('firebase/database');
    const [errSnap, actSnap, accessSnap] = await Promise.all([
      get(query(dbRef(db, 'logs/errors'),  orderByChild('timestamp'), limitToLast(100))),
      get(query(dbRef(db, 'logs/actions'), orderByChild('timestamp'), limitToLast(100))),
      get(query(dbRef(db, 'logs/access'),  orderByChild('timestamp'), limitToLast(300))),
    ]);
    _allErrors  = Object.values((errSnap.val()  ?? {}) as Record<string, LogRecord>)
      .sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));
    _allActions = Object.values((actSnap.val()  ?? {}) as Record<string, LogRecord>)
      .sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));
    _allAccess = Object.values((accessSnap.val() ?? {}) as Record<string, LogRecord>)
      .sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));
    renderFilteredLogs();
  } catch (e) {
    if (errEl) errEl.innerHTML = '<div class="empty">Erro ao carregar logs.</div>';
    if (actEl) actEl.innerHTML = '<div class="empty">Erro ao carregar logs.</div>';
    if (accessEl) accessEl.innerHTML = '<div class="empty">Erro ao carregar acessos.</div>';
    toastError('Erro: ' + (e instanceof Error ? e.message : String(e)));
  }
}

export function filterLogs(): void {
  renderFilteredLogs();
}

function renderFilteredLogs(): void {
  const search     = ((document.getElementById('log-search')  as HTMLInputElement)?.value  ?? '').toLowerCase();
  const levelFilt  = ((document.getElementById('log-level')   as HTMLSelectElement)?.value ?? '');
  const actionFilt = ((document.getElementById('log-action')  as HTMLSelectElement)?.value ?? '');
  const dateFilt   = ((document.getElementById('log-date')    as HTMLInputElement)?.value  ?? '');

  const apply = (entries: LogRecord[]) => entries.filter(e => {
    if (levelFilt  && e.level  !== levelFilt)  return false;
    if (actionFilt && e.action !== actionFilt) return false;
    if (dateFilt) {
      const d = e.timestamp ? new Date(e.timestamp).toISOString().slice(0, 10) : '';
      if (d !== dateFilt) return false;
    }
    if (search && !(
      (e.message ?? '').toLowerCase().includes(search) ||
      (e.email   ?? '').toLowerCase().includes(search) ||
      (e.action  ?? '').toLowerCase().includes(search)
    )) return false;
    return true;
  });

  const errEl = document.getElementById('logs-errors-list');
  const actEl = document.getElementById('logs-actions-list');
  const accessEl = document.getElementById('logs-access-list');
  if (errEl) errEl.innerHTML = buildLogList(apply(_allErrors));
  if (actEl) actEl.innerHTML = buildLogList(apply(_allActions));
  if (accessEl) accessEl.innerHTML = buildLogList(apply(_allAccess));
}

function buildLogList(entries: LogRecord[]): string {
  if (!entries.length)
    return '<div class="empty" style="padding:.75rem">Nenhum registro encontrado.</div>';
  return entries.map(e => {
    const time  = e.timestamp
      ? new Date(e.timestamp).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' })
      : '—';
    const lvCls = e.level === 'error' ? 'b-danger' : e.level === 'warn' ? 'b-warn' : 'b-info';
    return `
      <div class="dash-log-row">
        <span class="badge ${lvCls}" style="font-size:10px;flex-shrink:0;white-space:nowrap">
          ${sanitize((e.action ?? '').replace(/_/g,' '))}
        </span>
        <div class="dash-log-body">
          <div class="dash-log-msg">${sanitize((e.message ?? '').slice(0,120))}</div>
          <div class="dash-log-meta">${sanitize(e.email ?? '—')} · ${time}</div>
        </div>
      </div>`;
  }).join('');
}
